import { FieldError } from "client/lib/apt-merchant";

import { Field } from "../../field";
import { Merchant } from "../../merchant";
import { MerchantField } from "../../types";

import {
  confirmAccountNumberError,
  determineSecondBankFormEligibility,
} from "./utils";
import { determineAccountApplicability } from "./utils";

export class ConfirmAccountField extends Field<string> {
  constructor(
    model: Merchant,
    { propertyName }: { propertyName: string },
    opts: Partial<MerchantField<string>> = {}
  ) {
    super(model, {
      dataType: "string",
      propertyName,
      uiFieldType: "Input",
      uiInputBaseProps: {
        inputMode: "numeric",
      },
      ...opts,
    });
  }

  get testValue(): any {
    const registeredCountry = this._model.companyInformation.registeredCountry
      .value;
    if (registeredCountry === "New Zealand") {
      return "12-3456-7890123-45";
    } else if (registeredCountry === "Australia") {
      return "A1123456";
    } else {
      return "4884448888";
    }
  }

  _formatOnChange = (initialVal: string): string => {
    const registeredCountry = this._model.lead.registeredCountry.value;

    if (registeredCountry !== "New Zealand") {
      return initialVal;
    }

    // NZ Bank Account numbers follow the following formats:
    // ##-####-#######-##  OR ##-####-#######-### (where # denotes number only)
    // Formatting here to ensure that format is consistent
    const initialValLength = initialVal.length;

    if (
      initialValLength > 14 &&
      initialVal.substring(2, 3) !== "-" &&
      initialVal.substring(6, 7) !== "-" &&
      initialVal.substring(13, 14) !== "-"
    ) {
      return (
        initialVal.substring(0, 2) +
        "-" +
        initialVal.substring(2, 6) +
        "-" +
        initialVal.substring(6, 13) +
        "-" +
        initialVal.substring(13)
      );
    }

    return initialVal;
  };

  get applicable(): boolean {
    const registeredCountry = this._model.companyInformation.registeredCountry
      .value;

    const salesChannel = this._model.storeDetails.salesChannel.value;

    if (this.propertyName === "confirmAccount2") {
      return determineSecondBankFormEligibility({
        salesChannel,
        registeredCountry,
      });
    }

    if (determineAccountApplicability(registeredCountry)) {
      return true;
    }
    return false;
  }

  get required(): boolean {
    if (this.propertyName === "confirmAccount2") {
      const secondaryBankInfo = this._model.secondaryBank;
      // don't require the field when all fields are left blank
      return (
        secondaryBankInfo.fields
          .filter((field) => field.shown)
          .some((field) => field.value) && this.applicable
      );
    }
    return this.applicable;
  }

  get shown(): boolean {
    return this.applicable;
  }

  _validate(): void {
    const accountValue =
      this.propertyName === "confirmAccount"
        ? this._model.primaryBank.account.value
        : this._model.secondaryBank.account2.value;
    if (accountValue !== this.value) {
      throw new FieldError(this, confirmAccountNumberError, "FieldMismatch");
    }
  }
}
